import { formatRoute } from 'constants/urls'
import { replace } from 'redux-first-history'

import { BarError } from 'classes/errors'
import { fromCallbacksToPromise } from 'helpers'
import { LOGIN, SIGNUP_SSO } from 'urls'
import { resumeStatePersistence } from 'app_modules/localStorage/localStorage'
import { notifyError } from 'app_modules/notifications/actions'
import * as sessionSelectors from 'app_modules/session/selectors'
import api from 'api'
import sessionActionTypes from 'app_modules/session/constants'
import { generateDpopKeyPair, createDpopProof } from 'utils/dpop'
import { saveDpopKeyPair } from 'utils/dpopKeyStore'

import { logout } from './fetchLogout'
import { setUser } from './setUser'

const { API_URL } = process.env

// Actions

export const fetchSSOLoginFailure = error => ({
  payload: error,
  type: sessionActionTypes.FETCH_SSO_LOGIN_FAILURE,
})

export const fetchSSOLoginRequest = () => ({
  type: sessionActionTypes.FETCH_SSO_LOGIN_REQUEST,
})

export const fetchSSOLoginSuccess = () => ({
  type: sessionActionTypes.FETCH_SSO_LOGIN_SUCCESS,
})

export const continueProvisioning = user => ({
  payload: user,
  type: sessionActionTypes.CONTINUE_PROVISIONING,
})

// Thunks

export const fetchSSOLogin = token => {
  const thunk = async (dispatch, getState) => {
    const loggedUser = sessionSelectors.user(getState())

    if (loggedUser.ssoToken !== token) {
      await Promise.all([dispatch(logout(true))])
      resumeStatePersistence()

      // Generate a fresh DPoP key pair before the SSO login request (mirrors fetchLogin.js).
      // The private key is non-extractable. It is held in Redux and persisted to IndexedDB
      // so a reload (e.g. the Stripe Checkout return) reuses it: the backend never rotates
      // a bound SSO session to a new key, so losing it would log the user out (EEDTD-101).
      // The SSO token is passed as accessToken so the ath claim is
      // included: the BE finds Token sso=<token> in the Authorization header, computes the
      // expected ath, and rejects the proof if it is missing (RFC 9449 §4.2).
      let keyPair = null
      let dpopProof = null
      try {
        keyPair = await generateDpopKeyPair()
        dpopProof = await createDpopProof(keyPair, 'GET', `${API_URL}/sessions/sso_login`, token)
      } catch {
        // WebCrypto unavailable — proceed without DPoP. BE will reject with 643 in enforce
        // mode; in soft mode the SSO login succeeds normally.
        keyPair = null
      }

      if (keyPair && dpopProof) {
        dispatch({ type: sessionActionTypes.SET_DPOP_KEY_PAIR, payload: { dpopKeyPair: keyPair } })
        // Saved after logout() above has finished clearing IndexedDB, so the clear cannot
        // delete the new key. Never rejects.
        await saveDpopKeyPair(keyPair)
      }

      return Promise.all([
        dispatch(fetchSSOLoginRequest()),
        fromCallbacksToPromise(api.session.ssoLogin, { token, dpopProof })
          .then(({ user }) => {
            const { hasOngoingProvisioning } = user.profiles[0]

            const newUser = {
              ...user,
              token: null,
            }

            if (hasOngoingProvisioning) {
              return Promise.all([dispatch(continueProvisioning(newUser)), dispatch(replace(formatRoute(SIGNUP_SSO)))])
            }

            return Promise.all([dispatch(fetchSSOLoginSuccess()), dispatch(setUser(newUser))])
          })
          .catch(({ error, isConnectionError }) => {
            dispatch(logout())
            if (!isConnectionError) {
              dispatch(notifyError(new BarError({ ...error, label: 'ssoLoginFailed' })))
            }
            dispatch(fetchSSOLoginFailure(error?.message))
            throw error
          }),
      ])
    }

    return dispatch(replace(formatRoute(LOGIN)))
  }

  thunk.type = sessionActionTypes.FETCH_SSO_LOGIN

  return thunk
}

export default fetchSSOLogin
